import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid" || res.data.status === "completed") {
          setStatus("success");
          return;
        }
        if (attempts < 15) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("success"); // Assume success after polling
        }
      } catch {
        if (attempts < 5) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("error");
        }
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#05030A" }}>
      <div className="noise-overlay" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-2xl p-10 sm:p-12 max-w-md w-full mx-6 text-center relative z-10"
      >
        {status === "checking" && (
          <>
            <div className="w-16 h-16 border-2 border-fuchsia-500 border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Processing payment...</h2>
            <p className="text-sm text-[#A19BB0]">Please wait while we confirm your subscription.</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2" data-testid="payment-success-title">
              Welcome aboard!
            </h2>
            <p className="text-sm text-[#A19BB0] mb-8">
              Your 14-day free trial has started. You'll receive a confirmation email shortly.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              data-testid="go-to-dashboard-button"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm text-white bg-gradient-to-r from-[#9D00FF] to-[#FF007F] hover:opacity-90 transition-opacity duration-200"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-sm text-[#A19BB0] mb-8">We couldn't confirm your payment. Please contact support.</p>
            <Link to="/" className="text-fuchsia-400 hover:text-fuchsia-300 text-sm underline">
              Back to home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
